/**
 * OptiVehicle Phase 3A — ADAS calibration advisory EVIDENCE (explanation only).
 *
 * The existing ADAS calibration add-on suggestion (addonSuggestionRules.ts) is
 * decided by model year + work keywords / alignment / job relationships. This
 * module never takes part in that decision. It runs AFTER the evaluator and may
 * only ATTACH an explanation to an 'adas' suggestion that already exists:
 * "Vehicle configuration reports: FCW, LDW".
 *
 *   - Only a resolved state of PRESENT counts (NHTSA "Standard"/"Yes" →
 *     Reported equipped, or a shop-entered "Equipped"), read through the
 *     canonical resolveProfile()/equipmentState() — never a new resolver.
 *   - Optional, Unknown and Not applicable never count.
 *   - Reported not equipped (or none of the facts) removes NOTHING: the
 *     suggestion, its pre-check and its triggers are returned untouched.
 *   - Evidence never creates a suggestion, never changes defaultChecked or
 *     matchedTriggers, and never adds work: the job is still added only by the
 *     person's "Add selected" click.
 *   - Configuration is factory/source-reported equipment, not an OE procedure:
 *     the copy says to verify the OEM calibration requirements.
 *
 * Data: the current vehicle_identity_facts rows already recorded for the vehicle
 * (one read, through RLS), or the decoded draft facts Create Ticket already holds.
 * It never calls NHTSA and never re-decodes. Nothing here is persisted — the
 * add-on outcome event records only types and triggers.
 */
import { supabase } from '@/integrations/supabase/client';
import {
  equipmentState, resolveProfile, type ResolvedFact, type StoredFact,
} from '../../supabase/functions/_shared/vehicleProfile/facts';
import type {
  AdasAdvisoryEvidence, AdasEvidenceCategory, AdasEvidenceItem, AddonSuggestion,
} from '@/types/addonSuggestions';

export type { AdasAdvisoryEvidence, AdasEvidenceCategory, AdasEvidenceItem };

/**
 * Conservative, explanation-only categories. Deliberately NOT included:
 * lighting (adaptive driving beam, auto high beam), the backup camera, and every
 * non-ADAS safety fact (ABS, ESC, traction control, TPMS, DRL, keyless).
 */
export const ADAS_ADVISORY_CATEGORIES: Readonly<Record<string, AdasEvidenceCategory>> = {
  adas_adaptive_cruise_control: 'forward',
  adas_forward_collision_warning: 'forward',
  adas_crash_imminent_braking: 'forward',
  adas_dynamic_brake_support: 'forward',
  adas_pedestrian_aeb: 'forward',
  adas_lane_departure_warning: 'lane',
  adas_lane_keeping_assist: 'lane',
  adas_lane_centering_assist: 'lane',
  adas_blind_spot_warning: 'side_rear',
  adas_blind_spot_intervention: 'side_rear',
  adas_rear_cross_traffic_alert: 'side_rear',
  adas_rear_aeb: 'side_rear',
  adas_parking_assist: 'parking',
};

/** Positive, calibration-relevant ADAS equipment from a resolved profile. Nothing else. */
export function adasAdvisoryEvidence(resolved: readonly ResolvedFact[]): AdasAdvisoryEvidence | null {
  const items = resolved
    .filter((r) => r.key in ADAS_ADVISORY_CATEGORIES && equipmentState(r.spec, r.value) === 'present')
    .map((r): AdasEvidenceItem => ({
      key: r.key,
      abbr: r.spec.abbr ?? r.spec.label,
      label: r.spec.label,
      category: ADAS_ADVISORY_CATEGORIES[r.key],
      sourceLabel: r.sourceLabel ?? 'Unknown source',
    }));
  if (items.length === 0) return null;
  const sources = new Set(items.map((i) => i.sourceLabel));
  return { items, commonSource: sources.size === 1 ? items[0].sourceLabel : null };
}

/**
 * Attach evidence to an EXISTING 'adas' suggestion only. Returns the same list
 * (same suggestions, same order, same defaultChecked / matchedTriggers) — with no
 * evidence, or with only negative / unknown evidence, it is returned as-is.
 */
export function attachAdasEvidence(
  suggestions: AddonSuggestion[],
  evidence: AdasAdvisoryEvidence | null,
): AddonSuggestion[] {
  if (!evidence || evidence.items.length === 0) return suggestions;
  return suggestions.map((s) => (s.type === 'adas' ? { ...s, evidence } : s));
}

/** Decoded draft facts (Create Ticket, before the vehicle exists) → evidence. */
export function adasEvidenceFromDecodedDraft(facts: ReadonlyArray<{ key: string; value: string }>): AdasAdvisoryEvidence | null {
  return adasAdvisoryEvidence(resolveProfile(facts.map((f): StoredFact => ({
    fact_key: f.key, value: f.value, source_class: 'decoded', source: 'nhtsa_vpic', confidence: 'partial',
  })), null));
}

/**
 * The vehicle's recorded facts → evidence. One RLS read of the current rows; no
 * external call. Fails OPEN to null: a missing explanation must never cost the
 * suggestion (or the ticket).
 */
export async function loadAdasAdvisoryEvidence(vehicleId: string | null | undefined): Promise<AdasAdvisoryEvidence | null> {
  if (!vehicleId) return null;
  try {
    const db = supabase as unknown as { from: (t: string) => any };
    const { data, error } = await db.from('vehicle_identity_facts')
      .select('fact_key, value, source_class, source, confidence, recorded_at')
      .eq('vehicle_id', vehicleId).is('superseded_at', null);
    if (error || !data) return null;
    return adasAdvisoryEvidence(resolveProfile(data as StoredFact[], null));
  } catch {
    return null;
  }
}
